/**
 * Store Locator JavaScript
 * Handles filtering, view switching, search functionality, and Google Maps integration
 */

class StoreLocator {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.section = document.getElementById(`store-locator-${sectionId}`);
    
    if (!this.section) {
      return;
    }

    this.data = window.storeLocatorData[`section-${sectionId}`];
    
    if (!this.data) {
      return;
    }

    this.stores = this.data.stores;
    this.settings = this.data.settings;
    this.translations = this.data.translations || {};
    this.allStores = []; // Will hold all stores fetched from API
    
    this.currentFilter = 'Fysisk butik';
    this.currentView = 'list';
    this.userLocation = null;
    this.map = null;
    this.markers = [];
    this.markerCluster = null;
    this.currentInfoWindow = null; // Track currently open info window

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.renderStores();
    
    // Add initial animations to visible cards
    this.animateInitialCards();
    
    // Fetch all stores for map view
    this.fetchAllStores();
    
    // Load Google Maps if API key is provided
    if (this.settings.apiKey && this.settings.apiKey.length > 0) {
      // Don't load immediately - wait for map view click
    }
  }

  animateInitialCards() {
    // Wait for DOM to be ready, then animate visible cards
    requestAnimationFrame(() => {
      const storeCards = this.section.querySelectorAll('.store-card:not(.hidden)');
      
      storeCards.forEach((card, index) => {
        // Add staggered animation delay
        setTimeout(() => {
          card.classList.add('animate-in');
        }, index * 50); // 50ms delay between each card
        
        // Remove animation class after it completes
        setTimeout(() => {
          card.classList.remove('animate-in');
        }, (index * 50) + 500); // Animation duration is 500ms
      });
    });
  }

  async fetchAllStores() {
    // Try to fetch all stores via Shopify's search API
    // This bypasses the 50-item limit for metaobjects
    try {
      const response = await fetch(`/search?q=*&type=forhandler&options[prefix]=last`);
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Parse store data from search results
        // This is a fallback - if search doesn't work, we use the initial stores
        console.log('Attempting to fetch all stores via search...');
      }
    } catch (error) {
      console.log('Using initial store data (limited to 50 items)');
    }
    
    // For now, use the stores we have
    // In a future update, you could create a dedicated endpoint to fetch all stores
    this.allStores = this.stores;
  }

  setupEventListeners() {
    // Filter buttons
    const filterButtons = this.section.querySelectorAll('.filter-btn');
    
    filterButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.handleFilterChange(e);
      });
    });

    // View toggle buttons
    const viewButtons = this.section.querySelectorAll('.view-toggle-btn');
    
    viewButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.handleViewChange(e);
      });
    });

    // Search functionality
    const searchBtn = this.section.querySelector('.store-search-btn');
    const searchInput = this.section.querySelector('.store-search-input');
    
    if (searchBtn && searchInput) {
      searchBtn.addEventListener('click', () => this.handleSearch());
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.handleSearch();
        }
      });
    }
  }

  handleFilterChange(e) {
    e.preventDefault();
    const btn = e.currentTarget;
    const filter = btn.dataset.filter;

    // Update active state
    this.section.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('header__active-menu-item'));
    btn.classList.add('header__active-menu-item');

    this.currentFilter = filter;
    this.renderStores();
  }

  handleViewChange(e) {
    e.preventDefault();
    const btn = e.currentTarget;
    const view = btn.dataset.view;

    // Update active state
    this.section.querySelectorAll('.view-toggle-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');

    // Toggle views
    this.section.querySelectorAll('.store-locator-view').forEach(v => v.classList.remove('active'));
    const targetView = this.section.querySelector(`[data-view-content="${view}"]`);
    
    if (targetView) {
      targetView.classList.add('active');
    }

    this.currentView = view;

    // Initialize map if switching to map view
    if (view === 'map') {
      if (!this.map) {
        // Check if Google Maps is already loaded
        if (window.google && window.google.maps) {
          setTimeout(() => this.initMap(), 100);
        } else {
          // Load Google Maps script first
          if (this.settings.apiKey && this.settings.apiKey.length > 10) {
            this.loadGoogleMaps();
          } else {
            console.error('Google Maps API key is missing or invalid');
          }
        }
      } else {
        // Map already exists, just trigger a resize
        setTimeout(() => {
          if (this.map && window.google && window.google.maps) {
            google.maps.event.trigger(this.map, 'resize');
          }
        }, 100);
      }
    }
  }

  renderStores() {
    const storeCards = this.section.querySelectorAll('.store-card');
    
    // Add filter transition class for smooth animations
    storeCards.forEach(card => {
      const isPhysical = card.dataset.isPhysical === 'true';
      const isWebshop = card.dataset.isWebshop === 'true';
      
      let shouldShow = false;
      
      // Filter logic
      if (this.currentFilter === 'all') {
        shouldShow = true;
      } else if (this.currentFilter === 'Fysisk butik' && isPhysical) {
        shouldShow = true;
      } else if (this.currentFilter === 'Webshop' && isWebshop) {
        shouldShow = true;
      }
      
      // Smooth transition for hiding
      if (!shouldShow && !card.classList.contains('hidden')) {
        card.classList.add('filter-transition', 'hiding');
        
        // Wait for fade out animation, then hide
        setTimeout(() => {
          card.classList.add('hidden');
          card.classList.remove('filter-transition', 'hiding');
        }, 300);
      } 
      // Smooth transition for showing
      else if (shouldShow && card.classList.contains('hidden')) {
        card.classList.remove('hidden');
        card.classList.add('filter-transition', 'hiding');
        
        // Force reflow to trigger animation
        void card.offsetWidth;
        
        // Remove hiding class to fade in
        requestAnimationFrame(() => {
          card.classList.remove('hiding');
          
          // Clean up transition class after animation
          setTimeout(() => {
            card.classList.remove('filter-transition');
          }, 300);
        });
      }
    });

    // Update markers on map if map exists
    if (this.map) {
      this.updateMapMarkers();
    }
  }

  async handleSearch() {
    const searchInput = this.section.querySelector('.store-search-input');
    if (!searchInput || !searchInput.value.trim()) return;

    const address = searchInput.value.trim();

    try {
      // Use Geocoding API to get coordinates
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&region=${this.settings.region}&key=${this.settings.apiKey}`
      );
      
      const data = await response.json();
      
      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        this.userLocation = location;
        
        // Calculate distances and sort
        this.calculateDistances(location);
        this.sortStoresByDistance();
        
        // If on map view, center map on user location
        if (this.currentView === 'map' && this.map) {
          this.map.setCenter(location);
          this.map.setZoom(12);
        }
      } else {
        alert(this.translations.searchError || 'Could not find the address. Please try again.');
      }
    } catch (error) {
      console.error('Search error:', error);
      alert(this.translations.searchErrorGeneric || 'An error occurred during the search. Please try again.');
    }
  }

  calculateDistances(userLocation) {
    const storeCards = this.section.querySelectorAll('.store-card');
    
    storeCards.forEach(card => {
      const lat = parseFloat(card.dataset.storeLat);
      const lng = parseFloat(card.dataset.storeLng);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        const distance = this.calculateDistance(
          userLocation.lat,
          userLocation.lng,
          lat,
          lng
        );
        
        card.dataset.distance = distance.toFixed(1);
        
        // Show distance in card
        const distanceEl = card.querySelector('.store-distance');
        const distanceText = card.querySelector('.distance-text');
        
        if (distanceEl && distanceText) {
          distanceEl.style.display = 'flex';
          const distanceString = this.translations.distance || '{{ distance }} km away';
          distanceText.textContent = distanceString.replace('{{ distance }}', distance.toFixed(1));
        }
      }
    });
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    // Haversine formula
    const R = 6371; // Radius of Earth in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  }

  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  sortStoresByDistance() {
    const storeList = this.section.querySelector('.store-list');
    if (!storeList) return;

    const cards = Array.from(storeList.querySelectorAll('.store-card'));
    
    cards.sort((a, b) => {
      const distA = parseFloat(a.dataset.distance) || Infinity;
      const distB = parseFloat(b.dataset.distance) || Infinity;
      return distA - distB;
    });

    // Re-append cards in sorted order
    cards.forEach(card => storeList.appendChild(card));
  }

  loadGoogleMaps() {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      if (this.currentView === 'map') {
        setTimeout(() => this.initMap(), 100);
      }
      return;
    }

    // Check if script is already being loaded
    if (window.googleMapsScriptLoading) {
      return;
    }

    window.googleMapsScriptLoading = true;

    // Set up callback
    window.initStoreLocatorMaps = () => {
      window.googleMapsScriptLoaded = true;
      window.googleMapsScriptLoading = false;
      
      // Initialize all map instances
      if (this.currentView === 'map') {
        setTimeout(() => this.initMap(), 100);
      }
    };

    // Create script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.settings.apiKey}&language=${this.settings.language}&region=${this.settings.region}&callback=initStoreLocatorMaps`;
    script.async = true;
    script.defer = true;
    
    script.onerror = () => {
      console.error('Failed to load Google Maps');
      window.googleMapsScriptLoading = false;
    };
    
    document.head.appendChild(script);
  }

  initMap() {
    if (!window.google || !window.google.maps) return;

    const mapElement = this.section.querySelector(`#store-map-${this.sectionId}`);
    if (!mapElement) return;

    // Get bounds for all physical stores (using allStores to show ALL markers)
    const bounds = new google.maps.LatLngBounds();
    const physicalStores = this.allStores.filter(store => 
      store.isPhysical && 
      store.latitude !== null && 
      store.longitude !== null
    );

    console.log(`Loading ${physicalStores.length} physical stores on map`);

    // Create map
    this.map = new google.maps.Map(mapElement, {
      zoom: this.settings.defaultZoom || 7,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    // Add markers for ALL physical stores
    this.markers = [];
    physicalStores.forEach((store, index) => {
      const position = {
        lat: store.latitude,
        lng: store.longitude
      };

      const marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: store.name,
        icon: this.getMarkerIcon()
      });

      // Add info window
      const infoWindow = new google.maps.InfoWindow({
        content: this.getInfoWindowContent(store)
      });

      marker.addListener('click', () => {
        // Close any currently open info window
        if (this.currentInfoWindow) {
          this.currentInfoWindow.close();
        }
        
        // Open new info window and track it
        infoWindow.open(this.map, marker);
        this.currentInfoWindow = infoWindow;
      });

      this.markers.push(marker);
      bounds.extend(position);
    });

    // Close info window when clicking on the map
    this.map.addListener('click', () => {
      if (this.currentInfoWindow) {
        this.currentInfoWindow.close();
        this.currentInfoWindow = null;
      }
    });

    // Fit bounds or use custom center
    if (this.settings.centerLat && this.settings.centerLng) {
      this.map.setCenter({
        lat: parseFloat(this.settings.centerLat),
        lng: parseFloat(this.settings.centerLng)
      });
    } else if (physicalStores.length > 0) {
      this.map.fitBounds(bounds);
    }

    // Load MarkerClusterer library if clustering is enabled
    if (this.settings.enableClustering && !window.markerClusterer) {
      this.loadMarkerClusterer();
    } else if (this.settings.enableClustering && window.markerClusterer) {
      // Clustering library already loaded, create clusterer
      this.createMarkerClusterer();
    }
  }

  loadMarkerClusterer() {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
    script.onload = () => {
      this.createMarkerClusterer();
    };
    document.head.appendChild(script);
  }

  createMarkerClusterer() {
    if (this.map && this.markers.length > 0) {
      // Create custom cluster renderer with gold color
      const renderer = {
        render: ({ count, position }) => {
          // ÆNDRE DENNE FARVE FOR AT STYLE CLUSTER MARKERS
          const clusterColor = '#B69958'; // Brand guld farve - ændr til din ønskede farve
          
          // Create cluster icon that mimics Google Maps style with your color
          const svg = `
            <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
              <!-- Outer circle with low opacity -->
              <circle cx="30" cy="30" r="28" fill="${clusterColor}" opacity="0.3"/>
              <!-- Middle circle with medium opacity -->
              <circle cx="30" cy="30" r="20" fill="${clusterColor}" opacity="0.5"/>
              <!-- Inner solid circle -->
              <circle cx="30" cy="30" r="15" fill="${clusterColor}" opacity="1"/>
              <!-- White text -->
              <text x="30" y="30" text-anchor="middle" dominant-baseline="central" 
                    fill="white" font-size="14" font-weight="bold" font-family="Arial, sans-serif">${count}</text>
            </svg>
          `;
          
          return new google.maps.Marker({
            position,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
              scaledSize: new google.maps.Size(60, 60),
              anchor: new google.maps.Point(30, 30)
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        }
      };

      this.markerCluster = new markerClusterer.MarkerClusterer({
        map: this.map,
        markers: this.markers,
        renderer: renderer
      });
    }
  }

  getMarkerIcon() {
    // Custom marker styling based on theme settings
    if (this.settings.pinImage) {
      return {
        url: this.settings.pinImage,
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 40)
      };
    }

    // Create custom pin icon as SVG (not a circle!)
    const pinColor = this.settings.pinBackground || '#B69958';
    const borderColor = this.settings.pinBorder || '#8B7648';
    
    const pinSvg = `
      <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24C32 7.163 24.837 0 16 0z" 
              fill="${pinColor}" stroke="${borderColor}" stroke-width="2"/>
        <circle cx="16" cy="15" r="5" fill="white"/>
      </svg>
    `;
    
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(pinSvg),
      scaledSize: new google.maps.Size(32, 40),
      anchor: new google.maps.Point(16, 40)
    };
  }

  getInfoWindowContent(store) {
    // Escape HTML to prevent XSS
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    // Check if mobile for responsive width
    const isMobile = window.innerWidth < 768;
    const maxWidth = isMobile ? 'calc(100vw - 80px)' : '320px';
    const minWidth = isMobile ? '260px' : '280px';

    let content = `
      <div style="padding: 16px; min-width: ${minWidth}; max-width: ${maxWidth}; font-family: system-ui, -apple-system, sans-serif;">
        <h3 style="margin: 0 0 12px 0; font-size: 18px; font-weight: 600; color: #000;">${escapeHtml(store.name)}</h3>
    `;

    // Address - show right after name
    if (store.address && typeof store.address === 'string' && store.address.trim() !== '') {
      const formattedAddress = escapeHtml(store.address).replace(/\n/g, '<br>');
      content += `<p style="margin: 0 0 12px 0; font-size: 14px; color: #666; line-height: 1.5;">${formattedAddress}</p>`;
    }

    // Type badges
    if (store.isPhysical || store.isWebshop) {
      content += `<div style="display: flex; gap: 6px; margin-bottom: 12px; flex-wrap: wrap;">`;
      
      if (store.isPhysical) {
        const physicalStoreLabel = this.translations.physicalStore || 'Physical store';
        content += `<span style="display: inline-block; padding: 4px 10px; background-color: ${this.settings.badgeBackground || '#000'}; color: white; font-size: 12px; font-weight: 500; border-radius: 4px;">${escapeHtml(physicalStoreLabel)}</span>`;
      }
      
      if (store.isWebshop) {
        const webshopLabel = this.translations.webshop || 'Webshop';
        content += `<span style="display: inline-block; padding: 4px 10px; background-color: #666; color: white; font-size: 12px; font-weight: 500; border-radius: 4px;">${escapeHtml(webshopLabel)}</span>`;
      }
      
      content += `</div>`;
    }

    // Links section
    const links = [];
    
    if (store.website) {
      const visitWebsiteLabel = this.translations.visitWebsite || 'Visit website';
      links.push(`
        <a href="${escapeHtml(store.website)}" target="_blank" rel="noopener" 
           style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; background: rgba(0, 0, 0, 0.05); color: #000; text-decoration: none; font-size: 14px; font-weight: 500; border-radius: 4px; transition: background 0.3s ease;"
           onmouseover="this.style.background='rgba(0, 0, 0, 0.1)'" 
           onmouseout="this.style.background='rgba(0, 0, 0, 0.05)'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
          </svg>
          ${escapeHtml(visitWebsiteLabel)}
        </a>
      `);
    }
    
    if (store.facebook) {
      const facebookLabel = this.translations.facebook || 'Facebook';
      links.push(`
        <a href="${escapeHtml(store.facebook)}" target="_blank" rel="noopener" 
           style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; background: rgba(0, 0, 0, 0.05); color: #000; text-decoration: none; font-size: 14px; font-weight: 500; border-radius: 4px; transition: background 0.3s ease;"
           onmouseover="this.style.background='rgba(0, 0, 0, 0.1)'" 
           onmouseout="this.style.background='rgba(0, 0, 0, 0.05)'">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M16 8.05A8 8 0 108.05 16V10.4H5.9V8.05h2.15V6.28c0-2.13 1.27-3.3 3.2-3.3.93 0 1.9.17 1.9.17v2.08h-1.07c-1.05 0-1.38.65-1.38 1.32v1.58h2.35l-.38 2.35H10.7V16A8 8 0 0016 8.05z" fill="currentColor"/>
          </svg>
          ${escapeHtml(facebookLabel)}
        </a>
      `);
    }
    
    if (store.latitude && store.longitude) {
      const getDirectionsLabel = this.translations.getDirections || 'Get directions';
      links.push(`
        <a href="https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}" target="_blank" rel="noopener" 
           style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; background: #000; color: #fff; text-decoration: none; font-size: 14px; font-weight: 500; border-radius: 4px; transition: background 0.3s ease;"
           onmouseover="this.style.background='#333'" 
           onmouseout="this.style.background='#000'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          ${escapeHtml(getDirectionsLabel)}
        </a>
      `);
    }

    if (links.length > 0) {
      content += `<div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px;">${links.join('')}</div>`;
    }

    content += '</div>';
    return content;
  }

  updateMapMarkers() {
    if (!this.map || this.markers.length === 0) return;

    // Filter markers based on current filter
    const physicalStores = this.stores.filter(s => s.isPhysical);
    
    this.markers.forEach((marker, index) => {
      const store = physicalStores[index];
      if (!store) return;

      if (this.currentFilter === 'all') {
        marker.setMap(this.map);
      } else if (this.currentFilter === 'Fysisk butik' && store.isPhysical) {
        marker.setMap(this.map);
      } else if (this.currentFilter === 'Webshop' && store.isWebshop) {
        // Don't show webshops on map
        marker.setMap(null);
      } else {
        marker.setMap(null);
      }
    });

    // Update cluster if it exists
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      const visibleMarkers = this.markers.filter(m => m.getMap() !== null);
      this.markerCluster.addMarkers(visibleMarkers);
    }
  }
}

// Initialize all store locators on the page
function initStoreLocators() {
  if (!window.storeLocatorData) {
    return;
  }
  
  Object.keys(window.storeLocatorData).forEach(key => {
    const sectionId = key.replace('section-', '');
    const element = document.getElementById(`store-locator-${sectionId}`);
    
    // Check if already initialized
    if (element && !element.dataset.initialized) {
      element.dataset.initialized = 'true';
      new StoreLocator(sectionId);
    }
  });
}

// Try multiple initialization methods to ensure it works
if (document.readyState === 'loading') {
  // DOM is still loading
  document.addEventListener('DOMContentLoaded', initStoreLocators);
} else {
  // DOM is already loaded
  initStoreLocators();
}

// Also try with a slight delay as backup
setTimeout(initStoreLocators, 100);
